'use client';

import type { PusherIntegrationConfigType } from '@unifyapps/defs/types/interface';
import { useEffect } from 'react';
import useInterfaceEventBus from '../../../hooks/useInterfaceEventBus';
import { usePusherChannelActions } from './hooks/pusher/useChannelActions';
import { usePusherMethods } from './hooks/pusher/useMethods';
import { usePusherSdkLifecycle } from './hooks/pusher/useSdkLifecycle';

export function PusherIntegration({
  pusherIntegrationConfig,
}: {
  pusherIntegrationConfig?: PusherIntegrationConfigType;
}) {
  const { interfaceEventBus } = useInterfaceEventBus();
  const { clientRef, ensureSdkLoaded, initializePusher, disconnectPusher } = usePusherSdkLifecycle({
    pusherIntegrationConfig,
    publishPusherCallback: interfaceEventBus.publish,
  });
  const { channelsRef, subscribeToChannel, unsubscribeFromChannel, resetChannel } =
    usePusherChannelActions({
      clientRef,
      pusherIntegrationConfig,
      publishPusherCallback: interfaceEventBus.publish,
    });
  const { handlePusherMethod } = usePusherMethods({
    initializePusher,
    subscribeToChannel,
    unsubscribeFromChannel,
    channelsRef,
    resetChannel,
  });

  useEffect(() => {
    if (!pusherIntegrationConfig?.enabled) {
      return;
    }

    void ensureSdkLoaded().catch((error) => console.error('Failed to load Pusher SDK', error));
    const unsubscribe = interfaceEventBus.subscribe('onPusherMethod', (payload) => {
      void handlePusherMethod(payload).catch((error) =>
        console.error(`Failed to handle Pusher method: ${payload.eventType}`, error),
      );
    });
    return () => {
      unsubscribe();
      disconnectPusher();
      resetChannel();
    };
  }, [
    ensureSdkLoaded,
    handlePusherMethod,
    interfaceEventBus,
    pusherIntegrationConfig?.enabled,
    disconnectPusher,
    resetChannel,
  ]);

  return null;
}
